/**
 * Reading a supplier invoice.
 *
 * Winnie's v7 note: Cost of Sales should capture the acquisition costs against a VIN,
 * and the platform should pick the details up off the invoice rather than have someone
 * retype them.
 *
 * This is the reading half, and it is deliberately a separate, pure function: text in,
 * fields out, no network, no database, no React. Every number it produces is a
 * SUGGESTION that a person confirms on screen. Nothing here writes an expense.
 *
 * It reads a text layer. A photographed or scanned invoice has no text layer, and this
 * says so rather than guessing, because a plausible wrong amount against the wrong VIN
 * is worse than an empty form.
 */

import { extractAbnFromText } from './abn'

export interface ParsedInvoiceLine {
  /** How this vehicle was named: 'vin:...' or 'stock:...'. Also the dedupe key. */
  key: string
  /** Empty when the invoice named the car by stock number instead. */
  vin: string
  stockNumber: string
  /** Null when the line names a vehicle but no amount could be read beside it. */
  amountCents: number | null
  text: string
}

export type InvoiceReadQuality = 'read' | 'partial' | 'unreadable'

export interface ParsedInvoice {
  supplier: string
  /**
   * The supplier's ABN as printed, digits only, or '' when the page carried none that
   * passed the checksum. Its own field, never folded into `supplier`: Winnie, 13 Sep —
   * "the ABN is recorded on its own field, distinct from the supplier name... This is
   * what lets future invoices from the same supplier match reliably even if the free-text
   * name varies" (Pinacorp / Pinacorp Pty Ltd / PINACORP).
   */
  supplierAbn: string
  invoiceRef: string
  /** ISO yyyy-mm-dd, or '' when no date could be read. */
  invoiceDate: string
  dueDate: string
  totalCents: number | null
  gstCents: number | null
  lines: ParsedInvoiceLine[]
  /* Every line of text the reader saw, in reading order. The vehicle lines above are only
     the ones a VIN or a stock number named; a car named by its plate alone is in here and
     nowhere else, and resolving a plate needs the plates we actually hold, which this file
     cannot know. So the text is handed on rather than thrown away. */
  textLines: string[]
  quality: InvoiceReadQuality
  /** Plain sentences for the screen: what was found, and what was not. */
  notes: string[]
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Australian invoices are day first. 03/04/2026 is 3 April, not 4 March. */
function readDate(chunk: string): string {
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(chunk)
  if (iso) return iso[1] + '-' + iso[2] + '-' + iso[3]

  const slash = /(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})/.exec(chunk)
  if (slash) {
    const d = Number(slash[1])
    const m = Number(slash[2])
    let y = Number(slash[3])
    if (y < 100) y += 2000
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) return y + '-' + pad2(m) + '-' + pad2(d)
  }

  const worded = /(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})/.exec(chunk)
  if (worded) {
    const m = MONTHS[worded[2].slice(0, 3).toLowerCase()]
    if (m) return worded[3] + '-' + pad2(m) + '-' + pad2(Number(worded[1]))
  }

  return ''
}

const AMOUNT = /(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})/g

function amountsIn(line: string): number[] {
  const out: number[] = []
  AMOUNT.lastIndex = 0
  let m = AMOUNT.exec(line)
  while (m) {
    out.push(Number(m[1].replace(/,/g, '')) * 100 + Number(m[2]))
    m = AMOUNT.exec(line)
  }
  return out
}

/**
 * A VIN is 17 characters and never uses I, O or Q. Requiring at least one digit keeps
 * an ordinary seventeen letter word out of the results.
 */
const VIN = /\b[A-HJ-NPR-Z0-9]{17}\b/g

const looksLikeVin = (candidate: string) => /\d/.test(candidate) && /[A-Z]/.test(candidate)

/**
 * The last money amount on a line, in cents, or null.
 *
 * A vehicle line's amount is the one printed beside it, and on an itemised invoice that is
 * the rightmost figure on the row. Exported so a line found by its plate reads its amount
 * the same way a line found by its VIN does.
 */
export function lastAmountIn(line: string): number | null {
  const found = amountsIn(String(line || ''))
  return found.length ? found[found.length - 1] : null
}

export function parseInvoiceText(raw: string): ParsedInvoice {
  const text = String(raw || '').replace(/\r/g, '')
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  const notes: string[] = []

  if (text.replace(/\s/g, '').length < 30) {
    return {
      supplier: '',
      supplierAbn: '',
      invoiceRef: '',
      invoiceDate: '',
      dueDate: '',
      totalCents: null,
      gstCents: null,
      lines: [],
      textLines: lines,
      quality: 'unreadable',
      notes: [
        'There is no text in this file to read. A photo or a scan needs the details typed in by hand.',
      ],
    }
  }

  // Supplier: the first line that reads like a business name rather than a heading.
  let supplier = ''
  for (const line of lines.slice(0, 12)) {
    const bare = line.replace(/[^A-Za-z ]/g, '').trim()
    if (bare.length < 3 || bare.length > 60) continue
    if (/^(tax )?invoice$/i.test(bare)) continue
    if (/^(statement|remittance|bill|account)$/i.test(bare)) continue
    supplier = line.replace(/\s{2,}/g, ' ').trim()
    break
  }
  if (!supplier) notes.push('Could not tell which line is the supplier name.')

  /* The supplier's ABN. Winnie, 13 Sep: recorded on its own field, and only when the
     digits pass the checksum - a failed checksum is an OCR misread, and a wrong ABN
     against a supplier is worse than no ABN at all, because it looks checked. */
  const supplierAbn = extractAbnFromText(text)
  if (!supplierAbn) notes.push('No supplier ABN could be read off this invoice.')

  // Invoice number. Scanned a line at a time, because across the whole blob the word
  // 'INVOICE' on its own heading line runs straight into the label below it and the
  // reference comes back as the word 'Invoice'. A real reference carries a digit, and a
  // date is not a reference.
  let invoiceRef = ''
  const REF = /(?:tax\s+invoice|invoice|inv|bill)\s*(?:no\.?|number|nbr|#|ref(?:erence)?)?\s*[:#]?\s*([A-Za-z0-9][A-Za-z0-9\-\/]{2,})/i
  for (const line of lines) {
    const m = REF.exec(line)
    if (!m) continue
    const candidate = m[1].trim()
    if (!/\d/.test(candidate)) continue
    if (/^\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}$/.test(candidate)) continue
    invoiceRef = candidate
    break
  }

  // Her transport invoice labels it Reference, not Invoice Number, and a Xero bill
  // often does the same. Same rules: it has to carry a digit and not be a date.
  if (!invoiceRef) {
    const REF2 = /\b(?:reference|ref|our\s+ref|your\s+ref)\s*[:#]?\s*([A-Za-z0-9][A-Za-z0-9\-\/]{2,})/i
    for (const line of lines) {
      const m = REF2.exec(line)
      if (!m) continue
      const candidate = m[1].trim()
      if (!/\d/.test(candidate)) continue
      if (/^\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}$/.test(candidate)) continue
      invoiceRef = candidate
      break
    }
  }
  if (!invoiceRef) notes.push('Could not find an invoice number.')

  // Dates. Anchored to their label rather than taken as the first date on the line:
  // her invoice prints Date, Due Date and Reference as three columns of one row, so a
  // line-level match handed the invoice date to both fields.
  let invoiceDate = ''
  let dueDate = ''
  const DATE_TEXT = '([0-9]{1,2}[\\/.-][0-9]{1,2}[\\/.-][0-9]{2,4}|[0-9]{1,2}\\s+[A-Za-z]{3,9}\\s+[0-9]{4}|[0-9]{4}-[0-9]{2}-[0-9]{2})'
  const labelled = (label: string) => new RegExp('(?:' + label + ')\\s*[:#]?\\s*' + DATE_TEXT, 'i')
  const DUE_RE = labelled('due\\s*date|payment\\s*due|due')
  const INV_RE = labelled('invoice\\s*date|tax\\s*date|dated|date')
  for (const line of lines) {
    if (!dueDate) {
      const m = DUE_RE.exec(line)
      if (m) dueDate = readDate(m[1])
    }
    if (!invoiceDate) {
      const m = INV_RE.exec(line)
      if (m) invoiceDate = readDate(m[1])
    }
  }
  if (invoiceDate && invoiceDate === dueDate) {
    // Both labels resolved to the same day, which means one of them matched the other's
    // date. The invoice date is the one we can trust; leave the due date empty.
    dueDate = ''
  }
  if (!invoiceDate) {
    for (const line of lines) {
      const d = readDate(line)
      if (d) {
        invoiceDate = d
        break
      }
    }
  }
  if (!invoiceDate) notes.push('Could not find an invoice date.')

  // Total. Lines that say total but not subtotal and not GST; largest wins, because on
  // most invoices the grand total is the biggest number on the page.
  let totalCents: number | null = null
  for (const line of lines) {
    if (!/\b(total|amount\s+due|balance\s+due|amount\s+payable)\b/i.test(line)) continue
    if (/\bsub\s*-?\s*total\b/i.test(line)) continue
    /* "Total GST" is a GST line. "Total payable including GST" is the invoice total, and
       Winnie's 10 Sep rule is that the purchase price comes off exactly that line. Skipping
       anything that merely mentions GST threw her own total away: a freight invoice reading
       "Total payable including GST  $1,200.00" came back with no total at all. A line is now
       only excluded when GST is what it is totalling, not when GST is a remark about what
       the total already contains. "Total ex GST" is still excluded. */
    if (/\b(?:total|amount)\s+(?:of\s+)?gst\b/i.test(line)) continue
    if (/\bgst\b/i.test(line) && !/\b(?:incl|inc|including|inclusive)\b/i.test(line)) continue
    const found = amountsIn(line)
    for (const cents of found) {
      if (totalCents === null || cents > totalCents) totalCents = cents
    }
  }
  if (totalCents === null) notes.push('Could not find the invoice total.')

  // GST.
  let gstCents: number | null = null
  for (const line of lines) {
    if (!/\b(gst|tax)\b/i.test(line)) continue
    if (/\btax\s+invoice\b/i.test(line)) continue
    const found = amountsIn(line)
    if (found.length) {
      gstCents = found[found.length - 1]
      break
    }
  }
  if (gstCents === null) notes.push('Could not find a GST amount.')

  // Vehicles. A line that names a VIN is a line about a car, and the last amount on it
  // is that car's share. Where the invoice does not itemise, the amount comes back null
  // and the person allocates it themselves.
  //
  // A VIN is the better key, but not every supplier prints one and not every car in the
  // platform has one recorded, so a line that says 'Stock 1205' is read too. The word
  // has to be there: a bare number on an invoice is far more likely to be a quantity, a
  // date or a part of the invoice number than a stock reference. The number itself can
  // be a single digit: the stock loaded from EasyCars is numbered from 1.
  const STOCK = /\b(?:stock|stk|unit)\s*(?:no\.?|number|#)?\s*:?\s*(\d{1,6})\b/i
  /* "Unit 7, 22 Kellow Place, Eastern Creek NSW 2766" is a street address, not stock 7 -
     and it sat in the carrier's own letterhead, so every invoice from them was being pinned
     to whichever car happened to be stock number 7. A line that reads like an address is not
     a vehicle line. */
  const ADDRESSY = /\b(?:road|rd|street|st|avenue|ave|place|pl|drive|dr|court|ct|highway|hwy|parade|pde|lane|way|crescent|cres|boulevard|blvd|nsw|vic|qld|sa|wa|tas|nt|act)\b/i
  const seen = new Set<string>()
  const out: ParsedInvoiceLine[] = []
  // Which text line each vehicle was found on, so an amount can be traced back to it.
  const foundOn: number[] = []
  const hasVin = lines.map((l) => {
    VIN.lastIndex = 0
    const m = VIN.exec(l.toUpperCase())
    return Boolean(m && looksLikeVin(m[0]))
  })

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const upper = line.toUpperCase()
    const stockMatch = STOCK.exec(line)
    const stockOnLine =
      stockMatch && /^unit/i.test(String(stockMatch[0]).trim()) && ADDRESSY.test(line)
        ? null
        : stockMatch
    let vinOnLine = false
    VIN.lastIndex = 0
    let m = VIN.exec(upper)
    while (m) {
      const vin = m[0]
      if (looksLikeVin(vin)) {
        vinOnLine = true
        const key = 'vin:' + vin
        if (!seen.has(key)) {
          seen.add(key)
          const found = amountsIn(line)
          out.push({
            key,
            vin,
            stockNumber: stockOnLine ? stockOnLine[1] : '',
            amountCents: found.length ? found[found.length - 1] : null,
            text: line,
          })
          foundOn.push(i)
        }
      }
      m = VIN.exec(upper)
    }

    if (!vinOnLine && stockOnLine) {
      const key = 'stock:' + stockOnLine[1]
      if (!seen.has(key)) {
        seen.add(key)
        const found = amountsIn(line)
        out.push({
          key,
          vin: '',
          stockNumber: stockOnLine[1],
          amountCents: found.length ? found[found.length - 1] : null,
          text: line,
        })
        foundOn.push(i)
      }
    }
  }

  // A description cell can run to several lines, with the amount printed once on the row
  // the cell starts. Winnie's own transport invoice does exactly this: the line reads
  // 'Fleet Purchase_Used ISUZU NNR45' with the amount beside it, and 'VIN#...' underneath
  // on its own row carrying nothing. Where a vehicle line has no amount, look back a few
  // rows for the nearest one that has not already been claimed by another vehicle.
  const claimed = new Set<number>()
  out.forEach((entry, n) => {
    if (entry.amountCents !== null) {
      claimed.add(foundOn[n])
      return
    }
    const from = foundOn[n]
    for (let back = from - 1; back >= 0 && back >= from - 3; back--) {
      if (hasVin[back] || claimed.has(back)) break
      const found = amountsIn(lines[back])
      if (found.length) {
        entry.amountCents = found[found.length - 1]
        claimed.add(back)
        break
      }
    }
  })

  if (!out.length) {
    notes.push('No VIN or stock number appears on this invoice, so the vehicles have to be chosen by hand.')
  }

  const gotHeader = Boolean(supplier && invoiceRef && totalCents !== null)
  const quality: InvoiceReadQuality = gotHeader && out.length ? 'read' : 'partial'

  return {
    supplier,
    supplierAbn,
    invoiceRef,
    invoiceDate,
    dueDate,
    totalCents,
    gstCents,
    lines: out,
    textLines: lines,
    quality,
    notes,
  }
}

/** Cents to the plain '1234.56' a form field wants. */
export const centsToField = (cents: number | null) =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2)
